import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../shared/services/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {SalesProfile} from '../../../shared/interface/sales-profile';
import {RegistrationService} from '../../../shared/services/registration.service';
import {HttpErrorResponse} from '@angular/common/http';
import {Dictionary} from '../../../shared/interface/dictionary';
import {InsuranceCompany} from '../../../shared/interface/insurance-company';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';

@Component({
  selector: 'app-all-sales-profiles-list',
  templateUrl: './all-sales-profiles-list.component.html',
  styleUrls: ['./all-sales-profiles-list.component.scss']
})
export class AllSalesProfilesListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() registration?: string = null;
  @Input() disableFilters = false;
  @Input() disableBreadcrumb = false;
  @Input() excludeColumns: string[] = [];
  @Input() reloadList?: ReplaySubject<any>;
  @Input() embeddedView = false;

  initialFilters: any;
  salesProfiles: SalesProfile[] = [];
  displayedColumns: string[] = ['logo', 'insuranceCompany', 'ofwca', 'registration', 'type', 'status', 'createdAt', 'note', 'actions'];
  totalData = 0;
  selectedInsuranceCompany?: InsuranceCompany;
  updateInProgress = false;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private registrationService: RegistrationService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.registrationService.salesProfileListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<SalesProfile>) => {
          this.salesProfiles = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      insuranceCompany: 'int',
      type: 'int',
      ofwca: 'string',
      registration: 'string',
      status: 'int',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.registration = this.registration ?? data.registration ?? null;

        this.displayedColumns = this.displayedColumns.filter((e: string) => {
          return this.excludeColumns.indexOf(e) === -1;
        });

        if (!this.embeddedView) {
          this.store
            .pipe(
              select(getCurrentRouteState),
              delay(0),
              distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
              takeUntil(this.destroyed$),
            )
            .subscribe((state: RouterStateUrl) => {
              this.parseFilters(state.params);
              this.paginator.pageIndex = state.params.page;
              this.paginator.pageSize = state.params.pageSize;
              this.loadListData();
            })
          ;
        } else {
          this.loadListData();
        }
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        if (this.embeddedView) {
          this.loadListData();
        } else {
          this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
            page: event.pageIndex,
            pageSize: event.pageSize,
            ...this.filtersForm.value
          })]);
        }
      });

    if (this.reloadList) {
      this.reloadList
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.loadListData());
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.registrationService.loadSalesProfileList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.registration) {
      filters.registration = this.registration;
    }

    return filters;
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...this.filtersForm.value,
      })]);
    }
  }

  downloadReport(): void {
    this.registrationService.downloadSalesProfileReport(this.buildFilters());
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
      }]);
    }
  }

  statusChanged(newStatus: Dictionary, salesProfile: SalesProfile): void {
    this.updateInProgress = true;
    const form = {
      status: newStatus.id,
      note: salesProfile.note
    };

    this.registrationService.updateSalesProfile(salesProfile.uuid, form)
      .subscribe(() => {
        this.snack.open('Pomyślnie zakutalizowano profil', '', {duration: 2000});
        this.updateInProgress = false;
        salesProfile.status = newStatus;
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy aktualizacji wystąpił błąd', error.status.toString(), {duration: 2000});
        this.updateInProgress = false;
      });
  }

  noteChanged($event: any, salesProfile: SalesProfile): void {
    this.updateInProgress = true;
    const form = {
      status: salesProfile.status.id,
      note: $event.target.value,
    };

    this.registrationService.updateSalesProfile(salesProfile.uuid, form)
      .subscribe(() => {
        this.snack.open('Pomyślnie zakutalizowano profil', '', {duration: 2000});
        this.updateInProgress = false;
        salesProfile.note = $event.target.value;
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy aktualizacji wystąpił błąd', error.status.toString(), {duration: 2000});
        this.updateInProgress = false;
      });
  }

  delete(salesProfile: SalesProfile): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć profil sprzedażowy?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.registrationService.deleteSalesProfile(salesProfile.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto profil', '', {duration: 2000});
            this.loadListData();
          });
      }
    });
  }
}
